import { defineStore } from 'pinia';
import { ref } from 'vue';
import serverFetch from '@globals/ServerFetch';
import { HelloResponse } from '@globals/types/Hello';

/**
 * Rola użytkownika
 */
type UserRole = ''
	| 'ROLE_ADMIN'
	| 'ADMIN_BONUS_MOD'
  | 'ROLE_CUSTOMER'
  | 'ROLE_OPERATOR'
  | 'ROLE_PARTNER'
  | 'ROLE_USER'
  | 'ROLE_MSG_CMS_EDIT'
  | 'ROLE_MSG_CMS_PUBLISH'
;

export const useUserStore = defineStore('user', () => {
	const state = {
		/** Identyfikator użytkownika. */
		userId: ref(0),
		/** Identyfikator wykonawcy. */
		customerId: ref(0),

		/** Czy użytkownik ma uprawnienia administratorskie? */
		isAdmin: ref(false),
		/** Czy użytkownik jest wykonawcą? */
		isCustomer: ref(false),
		/** Czy użytkownik jest wykonwacą Oferteo Express? */
		isExpressSpecialist: ref(false),
		/** Czy użytkownik uczestniczy w programie partnerskim? */
		isPartner: ref(false),
		/** Główna rola użytkownika */
		role: ref(''),
		/** Lista wszystkich ról użytkownika */
		roles: ref<string[]>([]),
		/** Czy użytkownik zalogował się przy użyciu hasła (zamiast np. linku autowlogowującego)? */
		isAuth: ref(false),

		/** Imię użytkownika */
		firstName: ref(''),
		/** Nazwisko użytkownika */
		lastName: ref(''),
		/** Imię i nazwisko użytkownika */
		fullName: ref(''),
		/** Imię użytkownika w wołaczu */
		vocativeName: ref(''),
		/** Nazwa firmy użytkownika */
		companyName: ref(''),
		/** Numer telefonu użytkownika */
		phone: ref(''),
		/** Adres email użytkownika */
		email: ref(''),
		/** Miasto siedziby użytkownika */
		city: ref(''),
		/** Obrazek profilowy użytkownika */
		avatar: ref(''),
		/** Numer NIP użytkownika */
		nip: ref({ country: 'PL', value: '', isIndividual: false }),
		/** OStatnio złożony RFP */
		lastRfpCity: ref(''),

		/** Adres wizytówki użytkownika */
		profileUrl: ref(''),
		/** Płeć użytkownika */
		sex: ref<'MALE' | 'FEMALE' | 'NEUTRAL'>('MALE'),
		/** Zdobyte przez wykonawcę odznaki */
		badges: ref<string[]>([]),
		/** Adres strony Najlepsi na której znajduje się wykonawca */
		bestBadgeUrl: ref(''),

		/** Procentowe wypełnienie profilu użytkownika. */
		profileCompletness: ref(0),
		/** Aktualny stan konta użytkownika. */
		accountBalance: ref(0),
		/** Maksymalna cena za odkrycie jednego zapytania w kategorii. */
		maxCategoryPrice: ref(0),
		/** Limit zwrotów */
		reversalLimit: ref(0),
		/** Informacja o postępie płatności w zewnętrznym serwisie. */
		paymentProgress: ref<'' | 'success' | 'fail'>(''),
		/** Adres email użytkownika przy przełączeniu konta. */
		loggedAs: ref(''),
		/** Liczba darmowych odkryć za wypełnienie onboardingu */
		onboardingFreeUncoversLeft: ref(0),
		/** Wariant A/B testu dla kafelka customera na BV - POB-941 */
		rfpCustomerCardABTestVariant: ref('A'),
	};

	const actions = {
		update(rsvp: HelloResponse) {
			const { user } = rsvp;
			if (!user) { return; }

			state.userId.value = user.idUser;
			state.customerId.value = user.idCustomer;

			state.isCustomer.value = user.idCustomer > 0;
			state.isAdmin.value = rsvp.role === 'ADMIN';
			state.isPartner.value = rsvp.role === 'ADMIN' || rsvp.role === 'CUSTOMER';
			state.isExpressSpecialist.value = user.expressSpecialist || false;
			state.isPartner.value = user.isPartner || false;
			state.role.value = rsvp.role || '';
			state.roles.value = user.allRoles || [];
			state.isAuth.value = user.isAuth || false;

			state.firstName.value = user.firstName || '';
			state.lastName.value = user.lastName || '';
			if (state.firstName.value || state.lastName.value) {
				const fullName = [];
				if (state.firstName.value) { fullName.push(state.firstName.value); }
				if (state.lastName.value) { fullName.push(state.lastName.value); }
				state.fullName.value = fullName.join(' ');
			}
			state.vocativeName.value = user.vocativeName || '';

			state.companyName.value = user.companyName || '';
			state.phone.value = user.phone || '';
			state.email.value = user.email || '';
			state.city.value = user.city || '';
			state.avatar.value = (user.avatar || '').indexOf('images/hero') > -1 ? '' : user.avatar || '';
			state.nip.value = user.nip || state.nip.value;
			state.lastRfpCity.value = user.lastRfpCity || '';

			state.profileUrl.value = user.profileUrl || '';
			state.sex.value = user.sex || 'MALE';
			state.badges.value = user.badges || [];
			state.bestBadgeUrl.value = user.bestUrl || '';

			state.profileCompletness.value = user.profileCompletness || 0;
			state.accountBalance.value = user.accountBalance || 0;
			state.maxCategoryPrice.value = user.maxCategoryPrice || 0;
			state.reversalLimit.value = user.reversalLimit || 0;
			state.paymentProgress.value = '';

			state.loggedAs.value = user.switchedToUsername || '';
			state.onboardingFreeUncoversLeft.value = user.onboardingFreeUncoversLeft || 0;
		},

		/**
		 * Dodaje lub usuwa zdjęcie profilowe użytkownika,
		 * jeżeli zostało ono zmienione.
		 */
		async changeAvatar(avatarField: string) {
			// Jeżeli nic się nie zmieniło - nic nie rób.
			if (avatarField === state.avatar.value) {
				return '';
			}

			state.avatar.value = avatarField || '';

			// Jeżeli był obrazek, a go nie ma - usuń go.
			if (!avatarField) {
				await serverFetch('/myoferteo/konto-json/delete-avatar.ofer', {}, 'POST');
				return 'delete-avatar';
			}

			// W przeciwnym razie - dodaj obrazek.
			const filename = `${Number(new Date())}_avatar.jpg`;
			const bs = atob(avatarField.split(',')[1]);
			const ms = avatarField.split(',')[0].split(':')[1].split(';')[0];
			const ab = new ArrayBuffer(bs.length);
			const ia = new Uint8Array(ab);
			for (let i = 0; i < bs.length; i++) {
				ia[i] = bs.charCodeAt(i);
			}
			await serverFetch(
				'/myoferteo/konto-json/upload-avatar.ofer',
				{ file: new Blob([ab], { type: ms }), originalName: filename },
				'POST',
			);

			// Backend nie zwraca prawidłowej ścieżki po wysłaniu `upload-avatar.ofer`, dlatego też niezbędne jest pobranie obrazka z `hello.ofer`.
			serverFetch('/profile/hello.ofer').then((rsvp) => {
				state.avatar.value = rsvp.user.avatar;
			});
			return 'upload-avatar';
		},

		async logout() {
			await fetch(state.loggedAs.value ? '/j_acegi_exit_user' : '/j_acegi_logout');
			document.location.href = '/';
		},

		/**
		 * Otwiera okno z podglądem wizytówki użytkownika.
		 * Na appce iOS otwiera wizytówkę w tej samej karcie.
		 */
		displayProfilePreview() {
			const url = (state.profileUrl.value || `/podglad-wizytowki/firma/${state.customerId.value}`);
			try {
				if (navigator.userAgent === 'appstore.oferteo') {
					throw new Error('Opening new windows is not supported on iOS App');
				}
				window.open(url, '_blank');
			} catch (err) {
				window.location.href = url;
			}
		},

		/**
		 * Czy użytkownik posiada daną rolę?
		 */
		hasRole(role: UserRole) {
			return state.roles.value.includes(role);
		},
	};

	const getters = {};

	return { ...state, ...actions, ...getters };
});
